import { Box, Flex, Stack, useMediaQuery } from '@chakra-ui/react';
import { PromoGroupCount, PromoHighlights } from '@diamond/shared/types';
import { IconButton } from '@diamond/shared/ui';
import { promoValidFromToFormat, trackEvent } from '@diamond/shared/utils';
import { TRACKING_EVENTS, TRACKING_PAGES } from '@diamond/sol-buyer/constant';
import {
  SCROLL_DEBOUNCE_TIME,
  useShipToStore,
  useViewportTracking,
} from '@diamond/sol-buyer-context';
import { ArrowBackOutlined, ArrowForwardOutlined } from '@mui/icons-material';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import shallow from 'zustand/shallow';

import { CardProduct } from '../common';
import { BleedBackground } from '../layouts';
import { CardPromo } from './CardPromo';
import CardPromoRight from './CardPromoRight';

type ProductPromoProps = {
  data: PromoHighlights;
  slidesPerView?: number;
  count?: PromoGroupCount;
};

type ProductDetail = {
  id: string;
  brand_name: string;
  desc: string;
  stock: number;
};

export function ProductPromo({
  data,
  slidesPerView = 6,
  count,
}: ProductPromoProps) {
  const [selectedShipToId] = useShipToStore(
    (s) => [s.selectedShipToId],
    shallow
  );
  const router = useRouter();

  const [page, setPage] = useState(0);
  const offset = page * 100 * -1;
  const translateX = `calc(${offset}% - ${page * 2}px)`; // Adjusted to remove space when scrolling

  const [isMobile] = useMediaQuery('(max-width: 992px)');
  const refContainer = useRef<HTMLDivElement>(null);

  // Move dataProducts creation here so it's available for tracking
  const hasSpecificPromos = () => {
    const specificCount = count?.specific_promo_count;
    if (!specificCount) return false;
    if (specificCount === '0') return false;
    if (typeof specificCount === 'string' && specificCount.includes('+'))
      return true;
    return Number(specificCount) > 0;
  };

  const MAX_PRODUCTS = 24;

  const createStructuredProductArray = () => {
    const limitedProducts = data?.products?.slice(0, MAX_PRODUCTS) || [];
    const totalProducts = limitedProducts.length;
    let structured = [];

    if (isMobile) return data?.products ?? [];

    if (totalProducts <= 0) return [];

    if (totalProducts !== 12 && totalProducts !== 24) {
      return limitedProducts;
    }

    if (totalProducts <= 12) {
      const initialProducts = limitedProducts.slice(0, 10);
      structured.push(...initialProducts);

      const productsToRepeat = limitedProducts.slice(7, 10);
      structured.push(...productsToRepeat);

      if (limitedProducts.length > 10) {
        const remainingProducts = limitedProducts.slice(10, 13);
        structured.push(...remainingProducts);
      }

      if (structured.length < 15) {
        const missingCount = 15 - structured.length;
        const fillProducts = limitedProducts.slice(
          0,
          Math.min(missingCount, limitedProducts.length)
        );
        structured.push(...fillProducts);
      }

      return structured.slice(0, 15);
    }

    structured.push(...limitedProducts);

    if (totalProducts >= 19) {
      const productsToRepeat = limitedProducts.slice(16, 19);

      structured = [
        ...structured.slice(0, 19),
        ...productsToRepeat,
        ...structured.slice(19),
      ];
    }

    if (structured.length < 27) {
      const missingCount = 27 - structured.length;
      const fillProducts = limitedProducts.slice(
        0,
        Math.min(missingCount, limitedProducts.length)
      );
      structured.push(...fillProducts);
    }

    return structured.slice(0, 27);
  };

  const dataProducts = createStructuredProductArray();

  // Initialize viewport tracking hook
  const {
    sectionRef,
    trackPaginationChange,
    setupMobileScrollTracking,
    resetScrollFlag,
    setScrollFlag,
    forceTrackScrollEvents,
    isSectionInViewport,
  } = useViewportTracking(
    dataProducts,
    {
      sectionName: 'Promo Highlight',
      pageName: TRACKING_PAGES.BERANDA,
      listName: 'Promo Highlight',
      listId: `Promo Highlight | ${data?.promo?.promo_name} | ${data?.promo?.mechanism}`,
      promoSection: {
        promo_name: data?.promo?.promo_name,
        promo_code: data?.promo?.promo_code,
        mechanism: data?.promo?.mechanism ?? undefined,
      },
    },
    {
      intersectionThreshold: 0.65,
      trackIndividualItems: true,
      trackPromotionItems: true, // Enable promotion tracking
      scrollDebounceTime: SCROLL_DEBOUNCE_TIME, // Updated to SCROLL_DEBOUNCE_TIME
      // ms as requested
    }
  );

  // Reset scroll position when switching between mobile/desktop
  useEffect(() => {
    if (refContainer.current) {
      refContainer.current.scrollLeft = 0;
    }
    resetScrollFlag();
  }, [isMobile, resetScrollFlag]);

  // Setup mobile scroll tracking with 50% visibility threshold
  useEffect(() => {
    if (!isMobile || !refContainer.current) return;

    const cleanup = setupMobileScrollTracking(
      refContainer.current,
      (scrollLeft, clientWidth) => {
        // Track navigation event when scrolled to the end
        if (scrollLeft >= clientWidth) {
          trackEvent(TRACKING_EVENTS.NAVIGATE_PROMO, {
            section_name: 'Promo Highlight',
            page_name: TRACKING_PAGES.BERANDA,
          });
        }

        // Enhanced visibility calculation with 50% threshold for mobile
        const cardWidth = 218; // card width + padding
        const visibleProducts: typeof dataProducts = [];
        let startIndex = -1;

        // Check each product for 50% horizontal visibility
        dataProducts.forEach((product, index) => {
          const cardLeft = (index + 1) * cardWidth; // +1 to account for promo card
          const cardRight = cardLeft + cardWidth;
          const scrollRight = scrollLeft + clientWidth;

          // Calculate visible width of the card
          const visibleLeft = Math.max(cardLeft, scrollLeft);
          const visibleRight = Math.min(cardRight, scrollRight);
          const visibleWidth = Math.max(0, visibleRight - visibleLeft);
          const visibilityPercentage = visibleWidth / cardWidth;

          // Only include cards that are at least 65% visible
          if (visibilityPercentage >= 0.65) {
            if (startIndex === -1) {
              startIndex = index;
            }
            visibleProducts.push(product);
          }
        });

        return {
          products: visibleProducts,
          startIndex: startIndex >= 0 ? startIndex : 0,
        };
      }
    );

    return cleanup;
  }, [isMobile, setupMobileScrollTracking, dataProducts]);

  // Enhanced vertical scroll tracking with SCROLL_DEBOUNCE_TIME ms debounce
  useEffect(() => {
    if (isMobile || dataProducts.length === 0 || typeof window === 'undefined')
      return;

    let scrollTimeout: NodeJS.Timeout;
    // const trackedProductIds = new Set<string>();

    const getVisibleProducts = () => {
      if (!sectionRef.current || !isSectionInViewport) {
        return { products: [], startIndex: 0 };
      }

      const sectionRect = sectionRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // Check if section is at least 65% visible
      const visibleHeight =
        Math.min(sectionRect.bottom, viewportHeight) -
        Math.max(sectionRect.top, 0);
      const sectionVisibility = visibleHeight / sectionRect.height;

      if (sectionVisibility < 0.65) {
        return { products: [], startIndex: 0 };
      }

      // Calculate visible products based on current page
      const isFirstPage = page === 0;
      // let startIndex = isFirstPage ? 0: page+1 * slidesPerView;
      const startIndex = page === 1 ? 4 : 4 + (page - 1) * slidesPerView;
      const endIndex = startIndex + slidesPerView;
      const visibleProducts: typeof dataProducts = dataProducts.slice(
        isFirstPage ? 0 : startIndex,
        endIndex
      ); // Empty array of same type

      return { products: visibleProducts, startIndex };
    };

    const handleScroll = () => {
      setScrollFlag();
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const { products: visibleProducts, startIndex } = getVisibleProducts();

        if (visibleProducts.length === 0) return;

        forceTrackScrollEvents(visibleProducts, startIndex);
      }, SCROLL_DEBOUNCE_TIME); // SCROLL_DEBOUNCE_TIME ms debounce as requested
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dataProducts,
    sectionRef,
    isSectionInViewport,
    setScrollFlag,
    forceTrackScrollEvents,
    // page,
    // slidesPerView,
  ]);

  const handleNavigatePromo = useCallback(
    (page: number) => {
      setPage(page);
      trackEvent(TRACKING_EVENTS.NAVIGATE_PROMO, {
        section_name: 'Promo Highlight',
        page_name: TRACKING_PAGES.BERANDA,
      });
      if (!isMobile) {
        const isFirstPage = page === 0;
        const startIndex = page === 1 ? 4 : 4 + (page - 1) * slidesPerView;
        const endIndex = startIndex + slidesPerView;
        const visibleProducts = dataProducts.slice(
          isFirstPage ? 0 : startIndex,
          isFirstPage ? 4 : endIndex
        );

        if (visibleProducts.length > 0) {
          trackPaginationChange(visibleProducts, isFirstPage ? 0 : startIndex);
        }
      }
    },
    [dataProducts, isMobile, slidesPerView, trackPaginationChange]
  );

  async function handleClickProductCard(product: ProductDetail) {
    await router.push({
      pathname: `/product/${product.id}`,
      query: { warehouse: selectedShipToId },
    });
  }

  if (!data.promo) return null;

  return (
    <Flex
      flexDirection="column"
      position="relative"
      pt="5"
      pb="8"
      ref={sectionRef}
    >
      <BleedBackground
        bgColor="#00A0E8"
        bgImage="url('/images/tile-promo.png')"
        bgSize="120px"
      />
      <ProductPromoActionButtons
        page={page}
        pageCount={Math.floor(data.products.length / slidesPerView)}
        onNext={() => handleNavigatePromo(page + 1)}
        onPrevious={() => handleNavigatePromo(page - 1)}
      />

      <Flex
        flexDirection="column"
        ref={refContainer}
        id="refContainer"
        overflowX={{ base: 'scroll', lg: 'hidden' }}
        css={{
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
        }}
      >
        <Flex
          ml={{ base: '-4', lg: '-2' }}
          transform="auto-gpu"
          translateX={translateX}
          transition="transform 1.2s"
        >
          <Box
            pl={{ base: '4', lg: '2' }}
            flex={{
              // (Card highlight width + Gap (padding left)) x 2
              base: '0 0 calc((var(--card-width-carousel) + var(--chakra-space-4)) * 1.2)',
              lg: `0 0 calc((100% / ${slidesPerView}) * 2)`,
            }}
          >
            <CardPromo
              data={{
                id: data.promo.id,
                duration: `Periode ${promoValidFromToFormat(
                  data.promo.valid_from,
                  data.promo.valid_until,
                  false
                )}`,
                img: data.promo.banner_url,
                title: data.promo.promo_name,
                mechanism: data.promo.mechanism,
                termsAndCondition: data.promo.terms_and_conditions,
              }}
            />
          </Box>
          {dataProducts.map((product, i) => {
            const price =
              product.prices.length > 0 ? product.prices[0].price : 0;
            return (
              <Box
                pl={{ base: '4', lg: '2' }}
                flex={{
                  // Card highlight width + Gap (padding left)
                  base: '0 0 calc(var(--card-width-carousel) + var(--chakra-space-4))',
                  lg: `0 0 calc(100% / ${slidesPerView})`,
                }}
                key={product.id + i}
                data-product-index={i}
              >
                <CardProduct
                  productId={product.id}
                  imageUrl={product.product_images_medium?.[0]}
                  productName={product.brand_desc}
                  itemCode={product.item_code}
                  price={price}
                  unit={product.pack_unit}
                  description={product.title}
                  measurement_unit={product.sales_unit}
                  key={product.id}
                  name="product"
                  navigateTo={{
                    pathname: `/product/${product.id}`,
                    query: { warehouse: selectedShipToId },
                  }}
                  onClickTambahKeranjang={() => handleClickProductCard(product)}
                  stockStatus={product.stock > 0 ? 'AVAILABLE' : 'OUT_OF_STOCK'}
                  product={product}
                  section="Promo Highlight"
                  page={TRACKING_PAGES.BERANDA}
                  promoSection={{
                    promo_name: data?.promo?.promo_name,
                    promo_code: data?.promo?.promo_code,
                    mechanism: data?.promo?.mechanism ?? undefined,
                  }}
                />
              </Box>
            );
          })}

          <Box display="flex" width={'100%'} gap="2" flexDirection="column">
            {!hasSpecificPromos() ? null : (
              <CardPromoRight
                promoNumber={count?.specific_promo_count || 0}
                promoText="Promo Khusus"
                height="50%"
                bgPosition="left"
              />
            )}
            <CardPromoRight
              promoNumber={count?.general_promo_count || 0}
              promoText="Promo Lainnya"
              height={!hasSpecificPromos() ? '100%' : '50%'}
              bgPosition="right"
            />
          </Box>
        </Flex>
      </Flex>
    </Flex>
  );
}

type ProductPromoActionButtonsProps = {
  page: number;
  pageCount: number;
  onPrevious: () => void;
  onNext: () => void;
};

function ProductPromoActionButtons({
  page,
  pageCount,
  onNext,
  onPrevious,
}: ProductPromoActionButtonsProps) {
  return (
    <Stack
      spacing="4"
      direction="row"
      alignSelf="flex-end"
      // Container padding + IconButton height / 2 * -1
      mt="calc((var(--chakra-space-5) + (var(--chakra-sizes-10) / 2)) * -1)"
      mb="3"
      right="0"
      display={{ base: 'none', lg: 'block' }}
    >
      <IconButton
        aria-label="back-icon"
        name="back-icon"
        icon={<ArrowBackOutlined fontSize="small" />}
        variant="outline"
        borderWidth="1px"
        borderColor="blue"
        bg="white"
        color="blue"
        _disabled={{
          opacity: 1,
          cursor: 'not-allowed',
          color: 'gray.75',
          borderColor: 'gray.75',
          background: '#f7f7f7',
          _hover: {
            background: '#FAFAFA',
            opacity: 1,
          },
        }}
        isDisabled={page === 0}
        onClick={onPrevious}
      />
      <IconButton
        aria-label="next-icon"
        name="next-icon"
        icon={<ArrowForwardOutlined fontSize="small" />}
        variant="outline"
        borderWidth="1px"
        borderColor="blue"
        color="blue"
        bg="white"
        isDisabled={page === pageCount + 1}
        _disabled={{
          opacity: 1,
          cursor: 'not-allowed',
          color: 'gray.75',
          borderColor: 'gray.75',
          background: '#f7f7f7',
          _hover: {
            background: '#FAFAFA',
            opacity: 1,
          },
        }}
        onClick={onNext}
      />
    </Stack>
  );
}
